'use client';

import React from 'react';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';
import { cn } from '@/lib/utils';
import {
  availableBidIncrements,
  getLogicalBidIncrements,
} from '@/lib/utils/money';
import { Plus, Check, X } from 'lucide-react';

interface BidIncrement {
  value: number;
  label: string;
  denomination: string;
}

interface BidIncrementInputProps<T extends FieldValues> {
  label?: string;
  name: Path<T>;
  register: UseFormRegister<T>;
  error?: string;
  className?: string;
  value?: BidIncrement[];
  onChange?: (values: BidIncrement[]) => void;
  baseBudget?: number;
  disabled?: boolean;
}

export function BidIncrementInput<T extends FieldValues>({
  label,
  name,
  baseBudget,
  register,
  error,
  className,
  value = [],
  onChange,
  disabled = false,
}: BidIncrementInputProps<T>) {
  const availableIncrements = baseBudget
    ? getLogicalBidIncrements(baseBudget)
    : availableBidIncrements;

  const handleToggle = (increment: BidIncrement) => {
    if (disabled) return;

    const isSelected = value.some((v) => v.value === increment.value);
    const updatedValues = isSelected
      ? value.filter((v) => v.value !== increment.value)
      : [...value, increment].sort((a, b) => a.value - b.value);

    onChange?.(updatedValues);
  };

  return (
    <div className={cn('w-full', className, disabled && 'opacity-75')}>
      {label && (
        <label className="mb-3 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Selected Increments */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Selected Increments:
            </p>
            <div
              className={cn(
                'min-h-[100px] rounded-lg border border-gray-100 bg-gray-50 p-3',
                disabled && 'bg-gray-100'
              )}
            >
              {value.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {value.map((increment) => (
                    <div
                      key={increment.value}
                      className={cn(
                        'flex items-center space-x-1 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700',
                        disabled && 'cursor-not-allowed'
                      )}
                    >
                      <Check className="mr-1 h-3.5 w-3.5" />
                      <span>{increment.label}</span>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => handleToggle(increment)}
                          className="ml-1 rounded-full p-0.5 text-primary-500 hover:bg-primary-200 hover:text-primary-700"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic text-gray-400">
                  No increments selected
                </p>
              )}
            </div>
          </div>

          {/* Available Increments */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              Available Increments:
            </p>
            <div
              className={cn(
                'min-h-[100px] rounded-lg border border-gray-100 bg-gray-50 p-3',
                disabled && 'bg-gray-100'
              )}
            >
              <div className="flex flex-wrap gap-2">
                {availableIncrements.map((increment) => {
                  const isSelected = value.some(
                    (v) => v.value === increment.value
                  );
                  return (
                    <button
                      key={increment.value}
                      type="button"
                      onClick={() => handleToggle(increment)}
                      disabled={disabled}
                      className={cn(
                        'flex items-center rounded-full px-3 py-1 text-sm font-medium transition-all duration-200',
                        'focus:outline-none focus:ring-1 focus:ring-primary-500 focus:ring-offset-1',
                        isSelected
                          ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100',
                        disabled && 'cursor-not-allowed opacity-75'
                      )}
                    >
                      {isSelected ? (
                        <Check className="mr-1 h-3.5 w-3.5" />
                      ) : (
                        <Plus className="mr-1 h-3.5 w-3.5" />
                      )}
                      <span>{increment.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <input
        type="hidden"
        {...register(name)}
        value={JSON.stringify(value)}
        disabled={disabled}
      />
    </div>
  );
}
